import apiClient, { unwrapResponse } from './apiClient';
import { API_ENDPOINTS } from './endpoints';
import {
  IndividualDashboardData,
  ManagerDashboardData,
  User,
  Team,
  Notification,
  CalendarEvent,
  ApiResponse,
  TeamMemberWorkload,
} from '@/types';

// ============================================================================
// DASHBOARD SERVICE
// ============================================================================

export const dashboardService = {
  async getIndividualDashboard(): Promise<IndividualDashboardData> {
    const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.INDIVIDUAL);
    return unwrapResponse(response) as IndividualDashboardData;
  },

  async getManagerDashboard(): Promise<ManagerDashboardData> {
    const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.MANAGER);
    return unwrapResponse(response) as ManagerDashboardData;
  },

  async getProjectManagerDashboard(): Promise<any> {
    const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.PROJECT_MANAGER);
    return unwrapResponse(response);
  },

  async getAdminDashboard(): Promise<any> {
    const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.ADMIN);
    return unwrapResponse(response);
  },

  /** Requires teamId; backend is GET /task_management/teams/:teamId/workload */
  async getTeamWorkload(teamId: string): Promise<TeamMemberWorkload[] | unknown> {
    const response = await apiClient.get(API_ENDPOINTS.TEAMS.WORKLOAD(teamId));
    return unwrapResponse(response);
  },

  /**
   * Get team members with workload based on current user's role
   * Admin: Returns all non-admin users
   * Manager: Returns only direct reports (users where MANAGER_ID = current user)
   */
  async getTeamMembersWithWorkload(): Promise<TeamMemberWorkload[]> {
    const response = await apiClient.get(API_ENDPOINTS.USERS.TEAM_MEMBERS_WORKLOAD);
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },
};

// ============================================================================
// USER SERVICE
// ============================================================================

export const userService = {
  async getUsers(options?: { page?: number; pageSize?: number }): Promise<User[]> {
    const params = new URLSearchParams();
    if (options?.page != null) params.set('page', String(options.page));
    if (options?.pageSize != null) params.set('pageSize', String(options.pageSize));
    const url = params.toString() ? `${API_ENDPOINTS.USERS.LIST}?${params.toString()}` : API_ENDPOINTS.USERS.LIST;
    const response = await apiClient.get(url);
    const data = unwrapResponse<{ items?: User[] }>(response);
    return Array.isArray(data) ? data : (data?.items ?? []);
  },

  async getUser(userId: string): Promise<User | null> {
    const response = await apiClient.get(API_ENDPOINTS.USERS.GET(userId));
    return unwrapResponse(response) as User | null;
  },

  async searchUsers(query: string): Promise<User[]> {
    const response = await apiClient.get(API_ENDPOINTS.USERS.SEARCH, {
      params: { q: query },
    });
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },

  async getUsersByTeam(teamId: string): Promise<User[]> {
    const response = await apiClient.get(API_ENDPOINTS.USERS.BY_TEAM(teamId));
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },

  async getUsersByDepartment(departmentId: string): Promise<User[]> {
    const response = await apiClient.get(API_ENDPOINTS.USERS.BY_DEPARTMENT(departmentId));
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },

  async updateProfile(payload: {
    firstName?: string;
    lastName?: string;
    displayName?: string;
    timezone?: string;
    workHoursPerDay?: number;
  }): Promise<User> {
    const response = await apiClient.patch(API_ENDPOINTS.USERS.ME, payload);
    return unwrapResponse(response) as User;
  },

  async createUser(payload: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    displayName?: string;
    role: string;
    departmentId: string;
    teamId?: string;
    managerId?: string;
    workHoursPerDay?: number;
    timezone?: string;
    isActive?: boolean;
  }): Promise<User> {
    const response = await apiClient.post(API_ENDPOINTS.USERS.CREATE, payload);
    return unwrapResponse(response) as User;
  },

  async updateUser(userId: string, payload: {
    role?: string;
    isActive?: boolean;
    firstName?: string;
    lastName?: string;
    displayName?: string;
    departmentId?: string;
    teamId?: string | null;
    managerId?: string | null;
    workHoursPerDay?: number;
    timezone?: string;
  }): Promise<User> {
    const response = await apiClient.patch(API_ENDPOINTS.USERS.UPDATE(userId), payload);
    return unwrapResponse(response) as User;
  },

  async deleteUser(userId: string): Promise<User> {
    const response = await apiClient.delete(API_ENDPOINTS.USERS.DELETE(userId));
    return unwrapResponse(response) as User;
  },
};

// ============================================================================
// TEAM SERVICE
// ============================================================================

export const teamService = {
  async getTeams(): Promise<Team[] | { items: Team[] }> {
    const response = await apiClient.get(API_ENDPOINTS.TEAMS.LIST);
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : (data as { items: Team[] });
  },

  async getTeam(teamId: string): Promise<Team> {
    const response = await apiClient.get(API_ENDPOINTS.TEAMS.GET(teamId));
    return unwrapResponse(response) as Team;
  },

  async getTeamMembers(teamId: string): Promise<User[]> {
    const response = await apiClient.get(API_ENDPOINTS.TEAMS.MEMBERS(teamId));
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },

  async getTeamWorkload(teamId: string): Promise<any> {
    const response = await apiClient.get(API_ENDPOINTS.TEAMS.WORKLOAD(teamId));
    return unwrapResponse(response);
  },
};

// ============================================================================
// NOTIFICATION SERVICE
// ============================================================================

export const notificationService = {
  async getNotifications(): Promise<Notification[]> {
    const response = await apiClient.get(API_ENDPOINTS.NOTIFICATIONS.LIST);
    const data = unwrapResponse<{ items?: Notification[] } | Notification[]>(response);
    return Array.isArray(data) ? data : (data?.items ?? []);
  },

  async getUnreadNotifications(): Promise<Notification[]> {
    const response = await apiClient.get(API_ENDPOINTS.NOTIFICATIONS.UNREAD);
    const data = unwrapResponse<{ items?: Notification[] } | Notification[]>(response);
    return Array.isArray(data) ? data : (data?.items ?? []);
  },

  async markAsRead(notificationId: string): Promise<void> {
    await apiClient.patch(API_ENDPOINTS.NOTIFICATIONS.MARK_READ(notificationId));
  },

  async markAllAsRead(): Promise<void> {
    await apiClient.post(API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ);
  },

  async getPreferences(): Promise<Record<string, unknown>> {
    const response = await apiClient.get(API_ENDPOINTS.NOTIFICATIONS.PREFERENCES);
    return unwrapResponse<Record<string, unknown>>(response);
  },

  async updatePreferences(preferences: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await apiClient.patch(API_ENDPOINTS.NOTIFICATIONS.PREFERENCES, preferences);
    return unwrapResponse<Record<string, unknown>>(response);
  },
};

// ============================================================================
// CALENDAR SERVICE
// ============================================================================

export const calendarService = {
  /** Backend expects query params start, end (ISO date strings). */
  async getEvents(startDate: string, endDate: string): Promise<CalendarEvent[]> {
    const response = await apiClient.get(API_ENDPOINTS.CALENDAR.EVENTS, {
      params: { start: startDate, end: endDate },
    });
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },

  async getMyEvents(startDate: string, endDate: string): Promise<CalendarEvent[]> {
    const response = await apiClient.get(API_ENDPOINTS.CALENDAR.MY_EVENTS, {
      params: { start: startDate, end: endDate },
    });
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },

  async getTeamEvents(
    teamId: string,
    startDate: string,
    endDate: string
  ): Promise<CalendarEvent[]> {
    const response = await apiClient.get(
      API_ENDPOINTS.CALENDAR.TEAM_EVENTS(teamId),
      { params: { start: startDate, end: endDate } }
    );
    const data = unwrapResponse(response);
    return Array.isArray(data) ? data : [];
  },
};

export {
  dashboardService as default,
};
